import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MagnifyingGlassIcon, PlusIcon, UserCircleIcon, ClipboardIcon, ArrowPathIcon, XMarkIcon, CogIcon, Bars3Icon, KeyIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Button from '../common/Button';
import PasswordModal from './PasswordModal';
import AddItemModal from './AddItemModal';
import NotesModal from '../notes/CreateNoteModal';
import PersonalDetailsModal from '../personalDetails/PersonalDetailsModal';
import CardDetailsModal from '../cardDetails/CardDetailsModal';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { toast } from 'react-hot-toast';
import { useVault } from '../../contexts/VaultContext';
import BankDetailsModal from '../bankDetails/BankDetailsModal';
import DarkModeToggle from '../common/DarkModeToggle';
import { useSidebar } from '../../contexts/SidebarContext';
import ProfileSwitcher from '../profile/ProfileSwitcher';
import AddProfileModal from '../profile/AddProfileModal';

import { useWorkspace } from '../../contexts/WorkspaceContext';
import WorkspaceSidebar from '../workspace/WorkspaceSidebar';
import { useAppSelector } from '../../hooks/useRedux';
import UpgradePromptModal from '../common/UpgradePromptModal';
const IconButton = ({ onClick, icon: Icon, label, active = false }: {
    onClick: () => void;
    icon: any;
    label: string;
    active?: boolean;
}) => (
    <button
        onClick={onClick}
        className={`p-3 rounded-2xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/30 ${active
                ? 'bg-white/20 backdrop-blur-sm text-white border border-white/30 shadow-lg'
                : 'bg-white/10 backdrop-blur-sm text-white/80 hover:text-white hover:bg-white/20 border border-white/20'
            }`}
        aria-label={label}
    >
        <Icon className="w-5 h-5" />
    </button>
);

const VaultHeader: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isPasswordGeneratorOpen, setIsPasswordGeneratorOpen] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [includeLowercase, setIncludeLowercase] = useState(true);
    const [includeUppercase, setIncludeUppercase] = useState(true);
    const [includeNumbers, setIncludeNumbers] = useState(true);
    const [includeSymbols, setIncludeSymbols] = useState(true);
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [isPersonalDetailsModalOpen, setIsPersonalDetailsModalOpen] = useState(false);
    const [isCardDetailsModalOpen, setIsCardDetailsModalOpen] = useState(false);
    const [isBankDetailsModalOpen, setIsBankDetailsModalOpen] = useState(false);
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [isAddProfileModalOpen, setIsAddProfileModalOpen] = useState(false);
    const [isWorkspaceSidebarOpen, setIsWorkspaceSidebarOpen] = useState(false);
    const { activeWorkspace } = useWorkspace();
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const { team_id } = useAppSelector((state) => state.auth);
    const { user, logout } = useAuth();
    const { refreshEntries, searchQuery, setSearchQuery } = useVault();
    const { toggleSidebar, isOpen } = useSidebar();
    const SEARCH_DEBOUNCE_DELAY = 500;
    const MIN_SEARCH_CHARACTERS = 3;
    const [searchInputValue, setSearchInputValue] = useState(searchQuery);
    const [isSearching, setIsSearching] = useState(false);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastAppliedQueryRef = useRef<string>(searchQuery);


    const profileRef = useRef<HTMLDivElement>(null);
    const passwordPopoverRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    
    const scrollResultsIntoView = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const offset = (container: HTMLElement) => Math.min(container.clientHeight * 0.3, 320);
        const scrollContainer = document.querySelector('main.flex-1') as HTMLElement | null;

        window.requestAnimationFrame(() => {
            if (scrollContainer) {
                scrollContainer.scrollBy({ top: offset(scrollContainer), behavior: 'smooth' });
            } else {
                const defaultOffset = Math.min(window.innerHeight * 0.3, 320);
                window.scrollBy({ top: defaultOffset, behavior: 'smooth' });
            }
        });
    }, []);

    useOnClickOutside(profileRef, () => setIsProfileOpen(false));
    useOnClickOutside(passwordPopoverRef, () => setIsPasswordGeneratorOpen(false));

    useEffect(() => {
        setSearchInputValue(searchQuery);
        lastAppliedQueryRef.current = searchQuery;
    }, [searchQuery]);

    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        const trimmedValue = searchInputValue.trim();

        if (trimmedValue.length === 0) {
            if (lastAppliedQueryRef.current !== '') {
                setSearchQuery('');
                lastAppliedQueryRef.current = '';
            }
            setIsSearching(false);
            return;
        }

        if (trimmedValue.length < MIN_SEARCH_CHARACTERS) {
            setIsSearching(false);
            return;
        }

        if (trimmedValue === lastAppliedQueryRef.current) {
            setIsSearching(false);
            return;
        }

        setIsSearching(true);

        searchDebounceRef.current = setTimeout(() => {
            setSearchQuery(trimmedValue);
            lastAppliedQueryRef.current = trimmedValue;
            setIsSearching(false);
            scrollResultsIntoView();
        }, SEARCH_DEBOUNCE_DELAY);

        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, [MIN_SEARCH_CHARACTERS, SEARCH_DEBOUNCE_DELAY, scrollResultsIntoView, searchInputValue, setSearchQuery]);

    const handleLogout = () => {
        try {
            logout();
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Logout error:', error);
            toast.error('Failed to logout');
        }
    };

    const handleSuccess = async () => {
        setIsModalOpen(false);
        await refreshEntries();
        toast.success('Password added successfully');
    };

    const handleItemSelect = (itemType: string) => {
        if (itemType === 'password') {
            setIsModalOpen(true);
        } else if (itemType === 'notes') {
            setIsNotesModalOpen(true);
        } else if (itemType === 'personalDetails') {
            setIsPersonalDetailsModalOpen(true);
        } else if (itemType === 'cardDetails') {
            setIsCardDetailsModalOpen(true);
        } else if (itemType === 'bankDetails') {
            setIsBankDetailsModalOpen(true);
        }
        setIsItemModalOpen(false);
    };

    const generatePassword = () => {
        const chars = [];
        if (includeLowercase) chars.push(...Array.from('abcdefghijklmnopqrstuvwxyz'));
        if (includeUppercase) chars.push(...Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ'));
        if (includeNumbers) chars.push(...Array.from('0123456789'));
        if (includeSymbols) chars.push(...Array.from('!#$%^&*()_+'));

        if (chars.length === 0) return; // No characters selected

        let password = '';
        for (let i = 0; i < 16; i++) {
            password += chars[Math.floor(Math.random() * chars.length)];
        }
        setGeneratedPassword(password);
    };

    const copyToClipboard = async () => {
        await navigator.clipboard.writeText(generatedPassword);
        toast.success('Password copied to clipboard!');
    };

    useEffect(() => {
        generatePassword();
    }, [includeLowercase, includeUppercase, includeNumbers, includeSymbols]);

    return (
        <>
            <header className="w-full">
                <div className="relative z-10 flex-shrink-0 h-16 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 shadow-xl border-b border-white/20 flex">
                    <div className="flex-1 flex justify-between px-2 sm:px-4 md:px-6">
                        {/* Mobile Menu Button - Modern glassmorphism style */}
                        <div className="flex items-center lg:hidden">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center p-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white/80 hover:text-white hover:bg-white/20 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/30"
                                onClick={() => {
                                    console.log("Header button clicked");
                                    toggleSidebar();
                                }}
                                aria-expanded={isOpen}
                            >
                                <span className="sr-only">Open main menu</span>
                                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>

                        {/* Modern Search Bar - Enhanced design */}
                        <div className="hidden md:flex flex-1 items-center justify-start">
                            <div className="max-w-lg w-full">
                                <label htmlFor="search" className="sr-only">Search</label>
                                <div className="relative">
                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 z-10">
                                        <MagnifyingGlassIcon className="h-5 w-5 text-white" aria-hidden="true" />
                                    </div>
                                    <input
                                        id="search"
                                        name="search"
                                        className="block w-full rounded-2xl border-0 bg-white/10 py-3 pl-12 pr-4 text-white placeholder:text-white/60 ring-1 ring-inset ring-white/20 focus:ring-2 focus:ring-inset focus:ring-white/40 focus:bg-white/20 sm:text-sm sm:leading-6 transition-all duration-300"
                                        placeholder="Search your vault (min. 3 characters)..."
                                        type="text"
                                        value={searchInputValue}
                                        onChange={(e) => setSearchInputValue(e.target.value)}
                                    />
                                    {isSearching && (
                                        <div className="absolute inset-y-0 right-12 flex items-center z-10">
                                            <span className="h-4 w-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin"></span>
                                        </div>
                                    )}
                                    {searchInputValue && (
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 z-10">
                                            <button
                                                onClick={() => {
                                                    setSearchInputValue('');
                                                    setSearchQuery('');
                                                }}
                                                className="p-2 text-white font-bold hover:text-white rounded-lg hover:bg-white/20 transition-all duration-200"
                                                title="Clear search"
                                            >
                                                <XMarkIcon className="h-5 w-5 font-bold" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Mobile Search Button - Modern style */}
                        <div className="flex items-center md:hidden ml-2">
                            <button
                                type="button"
                                className="inline-flex items-center justify-center p-2 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white/80 hover:text-white hover:bg-white/20 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/30"
                                onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                            >
                                <MagnifyingGlassIcon className="h-5 w-5 text-white/80" aria-hidden="true" />
                            </button>
                        </div>

                        {/* Enhanced Mobile Search Overlay */}
                        <div className={`${isMobileSearchOpen ? 'flex absolute top-16 left-0 right-0 bg-gradient-to-r from-primary-600 via-primary-700 to-primary-800 z-20 p-4 shadow-2xl border-t border-white/20' : 'hidden'} md:hidden`}>
                            <div className="w-full flex">
                                <label htmlFor="mobile-search" className="sr-only">Search</label>
                                <div className="relative w-full">
                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                                        <MagnifyingGlassIcon className="h-5 w-5 text-white/60" aria-hidden="true" />
                                    </div>
                                    <input
                                        id="mobile-search"
                                        className="block w-full h-full pl-12 pr-12 py-3 bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/40 focus:bg-white/20 sm:text-sm rounded-2xl transition-all duration-300"
                                        placeholder="Search your vault (min. 3 characters)..."
                                        type="text"
                                        value={searchInputValue}
                                        onChange={(e) => setSearchInputValue(e.target.value)}
                                    />
                                    {isSearching && (
                                        <div className="absolute inset-y-0 right-10 flex items-center">
                                            <span className="h-4 w-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin"></span>
                                        </div>
                                    )}
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 z-10">
                                        <button
                                            onClick={() => setIsMobileSearchOpen(false)}
                                            className="p-2 text-white font-bold hover:text-white rounded-lg hover:bg-white/20 transition-all duration-200"
                                            title="Close search"
                                        >
                                            <XMarkIcon className="h-5 w-5 font-bold" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action buttons - optimized for all screen sizes */}
                        <div className="ml-auto flex items-center space-x-1 sm:space-x-2 md:space-x-3 lg:space-x-4">
                            {/* Tools section with consistent styling */}
                            <div className="flex items-center space-x-2">
                                {/* Check if the user is on a trial and has days remaining */}
                                {/* {user?.license_type === 'trial' && user.trial_days_remaining && user.trial_days_remaining > 0 && (
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => setShowUpgradeModal(true)}
                                            className="flex items-center justify-center px-4 py-2 border-2 border-blue-600 rounded-md text-sm font-semibold text-blue-600 bg-white hover:bg-blue-600 hover:text-white transition-all duration-200 transform hover:scale-105 dark:bg-gray-700 dark:text-white dark:border-white dark:hover:bg-blue-600 dark:hover:text-white"
                                        >
                                            Upgrade Now
                                        </button>
                                    </div>
                                )} */}
                                {/* Password Generator Icon */}
                                <div className="relative" ref={passwordPopoverRef}>
                                    <IconButton
                                        onClick={() => setIsPasswordGeneratorOpen(!isPasswordGeneratorOpen)}
                                        icon={CogIcon}
                                        label="Password Generator"
                                        active={isPasswordGeneratorOpen}
                                    />

                                    {/* Modern Password Generator Popover */}
                                    {isPasswordGeneratorOpen && (
                                        <div className="absolute right-0 mt-3 w-80 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl 
                                            ring-1 ring-black/5 dark:ring-white/10 z-[9999] transform transition-all duration-300 ease-out border border-white/20">
                                            <div className="p-6">
                                                <div className="flex items-center justify-between mb-6">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl">
                                                            <KeyIcon className="h-5 w-5 text-white" />
                                                        </div>
                                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                            Password Generator
                                                        </h3>
                                                    </div>
                                                    <button
                                                        onClick={() => setIsPasswordGeneratorOpen(false)}
                                                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                                                    >
                                                        <XMarkIcon className="h-5 w-5" />
                                                    </button>
                                                </div>

                                                {/* Modern Generated Password Display */}
                                                <div className="relative mb-6">
                                                    <div className="flex items-center bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-4 border border-gray-200 dark:border-gray-600">
                                                        <input
                                                            type="text"
                                                            value={generatedPassword}
                                                            readOnly
                                                            className="flex-1 bg-transparent border-none focus:ring-0 text-sm font-mono text-gray-900 dark:text-gray-100"
                                                        />
                                                        <button
                                                            onClick={copyToClipboard}
                                                            className="ml-3 p-2 text-gray-500 hover:text-white rounded-xl hover:bg-gradient-to-r hover:from-green-500 hover:to-green-600 transition-all duration-300 group"
                                                            title="Copy to clipboard"
                                                        >
                                                            <ClipboardIcon className="h-5 w-5 group-hover:text-white" />
                                                        </button>
                                                        <button
                                                            onClick={generatePassword}
                                                            className="ml-2 p-2 text-gray-500 hover:text-white rounded-xl hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-600 transition-all duration-300 group"
                                                            title="Generate new password"
                                                        >
                                                            <ArrowPathIcon className="h-5 w-5 group-hover:text-white" />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Modern Password Options */}
                                                <div className="space-y-4">
                                                    <label className="flex items-center p-3 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={includeLowercase}
                                                            onChange={() => setIncludeLowercase(!includeLowercase)}
                                                            className="rounded-lg border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 dark:border-gray-600 dark:bg-gray-700 h-5 w-5"
                                                        />
                                                        <span className="ml-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Lowercase letters (a-z)
                                                        </span>
                                                    </label>
                                                    <label className="flex items-center p-3 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={includeUppercase}
                                                            onChange={() => setIncludeUppercase(!includeUppercase)}
                                                            className="rounded-lg border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 dark:border-gray-600 dark:bg-gray-700 h-5 w-5"
                                                        />
                                                        <span className="ml-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Uppercase letters (A-Z)
                                                        </span>
                                                    </label>
                                                    <label className="flex items-center p-3 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={includeNumbers}
                                                            onChange={() => setIncludeNumbers(!includeNumbers)}
                                                            className="rounded-lg border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 dark:border-gray-600 dark:bg-gray-700 h-5 w-5"
                                                        />
                                                        <span className="ml-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Numbers (0-9)
                                                        </span>
                                                    </label>
                                                    <label className="flex items-center p-3 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={includeSymbols}
                                                            onChange={() => setIncludeSymbols(!includeSymbols)}
                                                            className="rounded-lg border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 dark:border-gray-600 dark:bg-gray-700 h-5 w-5"
                                                        />
                                                        <span className="ml-4 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Symbols (!@#$%^&*)
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Dark Mode Toggle with consistent styling */}
                                <DarkModeToggle className="bg-gray-100 dark:bg-gray-700" />
                            </div>

                            {/* Add Item Button - Text on larger screens, icon on small */}
                            {/* <Button
                                onClick={() => setIsItemModalOpen(true)}
                                type="button"
                                className="hidden sm:flex items-center px-3 py-1.5 md:px-4 md:py-2"
                            >
                                <PlusIcon className="h-4 w-4 sm:mr-1 md:mr-2" />
                                <span className="hidden md:inline">Add Item</span>
                            </Button> */}
                            {/* <Button
                                onClick={() => setIsItemModalOpen(true)}
                                type="button"
                                className="sm:hidden p-2 min-w-0"
                                aria-label="Add Item"
                            >
                                <PlusIcon className="h-5 w-5" />
                            </Button> */}

                            {/* Modern Profile Dropdown Section */}
                            <div className="relative profile-dropdown" ref={profileRef}>
                                <button
                                    type="button"
                                    className="inline-flex p-3 items-center justify-start rounded-2xl gap-3 
                                       text-white/80 hover:text-white
                                       bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20
                                       transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/30"
                                    onClick={() => setIsWorkspaceSidebarOpen(true)}
                                >
                                    <div className="p-1 bg-white/20 rounded-xl">
                                        <BuildingOffice2Icon className="h-4 w-4" aria-hidden="true" />
                                    </div>
                                    <span className="text-sm font-medium truncate max-w-[180px]">
                                        {activeWorkspace?.name || "Workspaces"}
                                    </span>
                                </button>

                                {/* Modern Profile Switcher */}
                                {isProfileOpen && (
                                    <div className="origin-top-right absolute right-0 mt-3 w-72 rounded-3xl shadow-2xl bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl ring-1 ring-black/5 dark:ring-white/10 divide-y divide-gray-100 dark:divide-gray-700 z-50 border border-white/20">
                                        <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-3xl">
                                            <div className="flex items-center space-x-3">
                                                <div className="p-2 bg-white/20 rounded-xl">
                                                    <UserCircleIcon className="h-6 w-6 text-white" />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-medium text-white/80">Signed in as</p>
                                                    <p className="text-sm font-semibold text-white truncate max-w-[200px]">
                                                        {user?.email}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="px-2 py-2">
                                            <ProfileSwitcher
                                                currentUserId={user?.id || ''}
                                                onAddProfile={() => {
                                                    setIsProfileOpen(false);
                                                    setIsAddProfileModalOpen(true);
                                                }}
                                                onClose={() => setIsProfileOpen(false)}
                                            />
                                        </div>

                                        <div className="py-2 px-2">
                                            <button
                                                onClick={() => {
                                                    setIsProfileOpen(false);
                                                    navigate('/mfa-setup');
                                                }}
                                                className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 hover:text-blue-600 dark:hover:text-blue-400 rounded-2xl transition-all duration-300 flex items-center space-x-3"
                                            >
                                                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.623 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                                                    </svg>
                                                </div>
                                                <span>Setup MFA</span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setIsProfileOpen(false);
                                                    handleLogout();
                                                }}
                                                className="w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 dark:hover:from-red-900/20 dark:hover:to-pink-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-2xl transition-all duration-300 flex items-center space-x-3"
                                            >
                                                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-xl">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                                                    </svg>
                                                </div>
                                                <span>Logout</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Item Selection Modal */}
            <AddItemModal
                isOpen={isItemModalOpen}
                onClose={() => setIsItemModalOpen(false)}
                onSelect={handleItemSelect}
            />

            {/* Password Modal */}
            <PasswordModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={handleSuccess}
            />

            {/* Notes Modal */}
            <NotesModal
                isOpen={isNotesModalOpen}
                onClose={() => setIsNotesModalOpen(false)}
                onSuccess={async () => {
                    setIsNotesModalOpen(false);
                    await refreshEntries();
                    toast.success('Note added successfully');
                }}
            />

            {/* Personal Details Modal */}
            <PersonalDetailsModal
                isOpen={isPersonalDetailsModalOpen}
                onClose={() => setIsPersonalDetailsModalOpen(false)}
                onSuccess={async () => {
                    setIsPersonalDetailsModalOpen(false);
                    await refreshEntries();
                    toast.success('Personal details added successfully');
                }}
            />

            {/* Card Details Modal */}
            <CardDetailsModal
                isOpen={isCardDetailsModalOpen}
                onClose={() => setIsCardDetailsModalOpen(false)}
                onSuccess={async () => {
                    setIsCardDetailsModalOpen(false);
                    await refreshEntries();
                    toast.success('Card details added successfully');
                }}
            />

            {/* Bank Details Modal */}
            <BankDetailsModal
                isOpen={isBankDetailsModalOpen}
                onClose={() => setIsBankDetailsModalOpen(false)}
                onSuccess={async () => {
                    setIsBankDetailsModalOpen(false);
                    await refreshEntries();
                    toast.success('Bank details added successfully');
                }}
            />

            {/* Add Profile Modal */}
            <AddProfileModal
                isOpen={isAddProfileModalOpen}
                onClose={() => setIsAddProfileModalOpen(false)}
                onSuccess={() => {
                    // This will be called after successfully adding a profile
                    toast.success('Profile added successfully');
                }}
            />

            {/* Workspace Sidebar */}
            <WorkspaceSidebar
                isOpen={isWorkspaceSidebarOpen}
                onClose={() => setIsWorkspaceSidebarOpen(false)}
            />

            <UpgradePromptModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                onUpgrade={() => {
                    navigate(`/upgrade?teamId=${team_id}`);
                }}
                teamId={team_id || ''}
            />
        </>
    );
};

export default VaultHeader;